import { z } from 'zod';
export const MoneySchema = z.object({
    amount: z.number(),
    currency: z.string().min(3).max(3),
});
export const ViewIdSchema = z.enum(['front', 'back', 'left', 'right', 'sleeve', 'other']);
export const PrintMethodSchema = z.enum(['dtg', 'dtf', 'sublimation']);
export const UnitSchema = z.literal('mm');
export const RectMmSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
    unit: UnitSchema.default('mm'),
});
export const ImageMaskSchema = z.enum([
    'none',
    'circle',
    'square',
    'rounded-square',
    'oval-h',
    'oval-v',
    'cloud',
    'flower',
    'distressed',
    'distressed-square',
    'distressed-h',
    'heart',
    'love',
    'paint',
    'label',
    'star',
    'star2',
    'rect-4-5',
    'rect-3-4',
    'rect-2-3',
    'rect-1-2',
    'rect-5-4',
    'rect-4-3',
    'rect-3-2',
    'rect-2-1',
    'brush',
]);
/** Color filters (Fabric.js-compatible matrices; same family Fancy Product Designer uses). */
export const ImageFilterSchema = z.enum([
    'none',
    'grayscale',
    'sepia',
    'vintage',
    'kodachrome',
    'technicolor',
    'blackwhite',
    'cold',
    'old',
    'milk',
    'purple',
    'yellow',
]);
export const TextAlignSchema = z.enum(['left', 'center', 'right']);
/** Straight or arc along a circle (radius in mm). */
export const TextCurveSchema = z.enum(['none', 'arc-up', 'arc-down']);
/** Baked glyph positions in mm (editor coordinates) — source of truth for WYSIWYG curve. */
export const CurveGlyphSchema = z.object({
    char: z.string(),
    x: z.number(),
    y: z.number(),
    rotation: z.number(),
});
const BaseObjectSchema = z.object({
    id: z.string().min(1),
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
    unit: UnitSchema.default('mm'),
    rotation: z.number().default(0),
    opacity: z.number().min(0).max(1).default(1),
    locked: z.boolean().default(false),
});
export const TextObjectSchema = BaseObjectSchema.extend({
    type: z.literal('text'),
    content: z.string().max(200),
    fontFamily: z.string().min(1),
    fontSize: z.number().positive(), // mm (cap height approx via fontSize mapping)
    fontWeight: z.union([z.number(), z.string()]).default(400),
    color: z.string().min(1),
    align: TextAlignSchema.default('center'),
    lineHeight: z.number().positive().default(1.2),
    curve: TextCurveSchema.default('none'),
    /** Circle radius for arc text (mm). */
    curveRadius: z.number().positive().default(80),
    /**
     * Glyph centers + rotation in mm relative to the text object's top-left.
     * When set, canvas / preview / print all draw this layout (immutable WYSIWYG).
     */
    curveGlyphs: z.array(CurveGlyphSchema).optional(),
});
export const ImageObjectSchema = BaseObjectSchema.extend({
    type: z.literal('image'),
    /** User-uploaded asset. Optional when galleryCode is set. */
    assetId: z.string().min(1).optional(),
    /** Catalog gallery item code — reference without duplicating as Asset. */
    galleryCode: z.string().min(1).optional(),
    mask: ImageMaskSchema.default('none'),
    /** Zoom of photo inside mask (1 = cover). */
    maskScale: z.number().min(1).max(5).default(1),
    /** Pan inside mask, −1…1 (0 = centered). */
    maskOffsetX: z.number().min(-1).max(1).default(0),
    maskOffsetY: z.number().min(-1).max(1).default(0),
    flipX: z.boolean().default(false),
    flipY: z.boolean().default(false),
    effects: z
        .object({
        brightness: z.number().optional(),
        contrast: z.number().optional(),
        /** @deprecated prefer `filter: 'grayscale'` */
        grayscale: z.boolean().optional(),
        filter: ImageFilterSchema.optional(),
    })
        .optional(),
});
export const DesignObjectSchema = z
    .discriminatedUnion('type', [TextObjectSchema, ImageObjectSchema])
    .superRefine((val, ctx) => {
    if (val.type === 'image' && !val.assetId && !val.galleryCode) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'image requires assetId or galleryCode',
            path: ['assetId'],
        });
    }
});
export const ViewDesignSchema = z.object({
    viewId: ViewIdSchema,
    printAreaId: z.string().min(1),
    objects: z.array(DesignObjectSchema).default([]),
});
export const DesignSelectionSchema = z.object({
    colorKey: z.string().min(1),
    /** Catalog hex from the storefront model (Woo). Used for server previews when template.colors lacks the key. */
    colorHex: z
        .string()
        .regex(/^#?[0-9a-fA-F]{6}$/)
        .optional(),
    sizeId: z.string().optional(),
    /** Print service: without white underbase vs with white underbase. */
    garmentPrintType: z.enum(['no_white_base', 'white_base']).optional(),
    /** Print service: reactive flannel / high-ink fabric surcharge. */
    franelaReactiva: z.boolean().optional(),
});
export const DesignDocumentSchema = z.object({
    version: z.literal(1),
    templateId: z.string().min(1),
    selection: DesignSelectionSchema,
    views: z.array(ViewDesignSchema).min(1),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
});
export function createEmptyDocument(templateId, colorKey, printAreaId = 'chest', colorHex, 
/** When set (from ProductModel.views), only those sides exist — e.g. mug = front only. */
viewIds) {
    const hex = colorHex?.replace(/^#/, '');
    const normalized = hex && /^[0-9a-fA-F]{6}$/i.test(hex) ? `#${hex.toLowerCase()}` : undefined;
    const ids = viewIds?.length ? viewIds : ['front', 'back'];
    return {
        version: 1,
        templateId,
        selection: {
            colorKey,
            ...(normalized ? { colorHex: normalized } : {}),
        },
        views: ids.map((viewId) => ({
            viewId,
            printAreaId: viewId === 'front' ? printAreaId : `${viewId}-main`,
            objects: [],
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}
//# sourceMappingURL=design.js.map